import { useEffect, useState } from "react";
import {
  buscarSesiones,
  cargarUsuarios,
  cargarNiveles,
  cargarGrados,
  cargarAreas,
  cargarRegiones,
  cargarProvincias,
  cargarDistritos,
  cargarIe,
} from "../sesion.search";

export const useSesionSearch = (userRole, userUbigeo, userInstitutionId = "") => {
  // filtros básicos
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [usuario, setUsuario] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  // nuevos filtros
  const [tipoSesion, setTipoSesion] = useState("");
  const [estado, setEstado] = useState("");

  // ubicacion IDs 
  const [regionId, setRegionId] = useState("");
  const [provinciaId, setProvinciaId] = useState("");
  const [distritoId, setDistritoId] = useState("");

  const [ieId, setIeId] = useState("");
  const [instituciones, setInstituciones] = useState([]);

  // ubicacion Nombres 
  const [regionNombre, setRegionNombre] = useState("");
  const [provinciaNombre, setProvinciaNombre] = useState("");
  const [distritoNombre, setDistritoNombre] = useState("");

  // pedagogico IDs
  const [nivelId, setNivelId] = useState("");
  const [gradoId, setGradoId] = useState("");
  const [areaId, setAreaId] = useState("");

  // Toggle de vista distrital
  const [vistaDistrital, setVistaDistrital] = useState(false);

  // data (catálogos)
  const [regiones, setRegiones] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [grados, setGrados] = useState([]);
  const [areas, setAreas] = useState([]);

  // resultados y paginación
  const [filtered, setFiltered] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const normalizedRole = (userRole || "").toUpperCase();
  const isAdmin = normalizedRole === "ADMIN";
  const isDirector = normalizedRole === "DIRECTOR";
  const isProfesor = normalizedRole === "PROFESOR";

  const authorInstitutionId = (() => {
    if (isAdmin) {
      return ieId || "";
    }

    if (isDirector) {
      if (!vistaDistrital) {
        return userInstitutionId || "";
      }

      return String(ieId) === String(userInstitutionId) ? (userInstitutionId || "") : "";
    }

    return "";
  })();

  const showAuthorFilter = !isProfesor && Boolean(authorInstitutionId);

  const filtrarDocentesPorInstitucion = (items, institucionId) =>
    (Array.isArray(items) ? items : []).filter((item) =>
      Array.isArray(item?.vinculos) &&
      item.vinculos.some(
        (vinculo) =>
          String(vinculo?.institucion_id) === String(institucionId) &&
          String(vinculo?.rol?.nombre || "").toLowerCase() === "profesor"
      )
    );

  /* =====================
     Cargas iniciales
  ===================== */

  useEffect(() => {
    cargarRegiones()
      .then(res => {
        let data = res.data || res || [];
        if (typeof data === "string") {
          data = JSON.parse(data);
        }
        setRegiones(data);
      })
      .catch(console.error);

    cargarNiveles().then(res => {
      let data = res.data || res || [];
      setNiveles(typeof data === "string" ? JSON.parse(data) : data);
    }).catch(console.error);
  }, [userRole]);

  useEffect(() => {
    const cargarAutores = async () => {
      if (!showAuthorFilter) {
        setUsuarios([]);
        return;
      }

      try {
        const pageSize = 100;
        let skip = 0;
        let total = 0;
        const acumulados = [];

        do {
          const response = await cargarUsuarios({
            institucion_id: authorInstitutionId,
            skip,
            limit: pageSize,
          });

          const items = filtrarDocentesPorInstitucion(response?.items, authorInstitutionId);
          acumulados.push(...items);
          total = Number(response?.total || 0);
          skip += pageSize;
        } while (skip < total);

        setUsuarios(acumulados);
      } catch (error) {
        console.error("Error cargando docentes por institución:", error);
        setUsuarios([]);
      }
    };

    cargarAutores();
  }, [authorInstitutionId, showAuthorFilter]);

  useEffect(() => {
    if (!showAuthorFilter) {
      setUsuario("");
      return;
    }

    if (usuario && !usuarios.some((item) => String(item.id) === String(usuario))) {
      setUsuario("");
    }
  }, [showAuthorFilter, usuario, usuarios]);

  /* =====================
     Cascada Geográfica y de IE
  ===================== */

  // 1. Cargar Provincias
  useEffect(() => {
    if (!regionId) {
      setProvincias([]);
      setProvinciaId("");
      setRegionNombre("");
      // SOLUCIÓN: Solo el ADMIN borra los colegios si se borra la región
      if (userRole === "ADMIN") {
        setInstituciones([]);
        setIeId("");
      }
      return;
    }

    const reg = regiones.find(r => r.id === regionId);
    setRegionNombre(reg ? reg.nombre : "");

    cargarProvincias(regionId).then(res => {
      let data = res.data || res || [];
      setProvincias(typeof data === "string" ? JSON.parse(data) : data);
      setDistritos([]);
      setDistritoId("");

      if (userRole === "ADMIN") {
        setInstituciones([]);
        setIeId("");
      }
    }).catch(console.error);
  }, [regionId, regiones, userRole]);

  // 2. Cargar Distritos
  useEffect(() => {
    if (!provinciaId) {
      setDistritos([]);
      setDistritoId("");
      setProvinciaNombre("");
      // SOLUCIÓN: Solo el ADMIN borra los colegios si se borra la provincia
      if (userRole === "ADMIN") {
        setInstituciones([]);
        setIeId("");
      }
      return;
    }
    const prov = provincias.find(p => p.id === provinciaId);
    setProvinciaNombre(prov ? prov.nombre : "");

    cargarDistritos(provinciaId).then(res => {
      let data = res.data || res || [];
      setDistritos(typeof data === "string" ? JSON.parse(data) : data);

      if (userRole === "ADMIN") {
        setInstituciones([]);
        setIeId("");
      }
    }).catch(console.error);
  }, [provinciaId, provincias, userRole]);

  // 3. Cargar Instituciones Educativas (Inteligente por Rol)
  useEffect(() => {
    const dist = distritos.find(d => d.id === distritoId);
    setDistritoNombre(dist ? dist.nombre : "");

    let targetUbigeo = null;

    if (userRole === "ADMIN") {
      if (distritoId) targetUbigeo = distritoId;
    } else {
      if (vistaDistrital && userUbigeo) targetUbigeo = userUbigeo;
    }

    if (!targetUbigeo) {
      setInstituciones([]);
      setIeId("");
      return;
    }

    cargarIe({ codigo_ubigeo: targetUbigeo })
      .then(res => {
        let data = res.data || res || [];
        setInstituciones(typeof data === "string" ? JSON.parse(data) : data);
      })
      .catch(console.error);

  }, [distritoId, distritos, vistaDistrital, userRole, userUbigeo]);

  /* =====================
     Cascada Pedagógica
  ===================== */

  useEffect(() => {
    if (!nivelId) {
      setGrados([]);
      setGradoId("");
      return;
    }

    cargarGrados(nivelId)
      .then(res => {
        setGrados(res.data);
        setAreas([]);
        setAreaId("");
      })
      .catch(console.error);
    // eslint-disable-next-line
  }, [nivelId]);

  useEffect(() => {
    if (!gradoId) {
      setAreas([]);
      setAreaId("");
      return;
    }

    cargarAreas(gradoId)
      .then(res => setAreas(res.data))
      .catch(console.error);
    // eslint-disable-next-line
  }, [gradoId]);

  /* =====================
     Búsqueda
  ===================== */

  const handleSearch = async (
    newPage = page,
    newRowsPerPage = rowsPerPage,
    overrideFilters = {}
  ) => {
    setLoading(true);
    try {
      const filtros = {};

      if (nivelId) filtros.nivel_id = nivelId;
      if (gradoId) filtros.grado_id = gradoId;
      if (areaId) filtros.area_tematica_id = areaId;
      if (showAuthorFilter && usuario) filtros.id_usuario = usuario;
      if (fechaDesde) filtros.fecha_inicio = fechaDesde;
      if (fechaHasta) filtros.fecha_fin = fechaHasta;
      if (search) filtros.q = search;

      if (tipoSesion) filtros.tipo_sesion = tipoSesion;
      if (estado) filtros.estado = estado;

      if (regionNombre) filtros.region = regionNombre;
      if (provinciaNombre) filtros.provincia = provinciaNombre;
      if (distritoNombre) filtros.distrito = distritoNombre;

      if (ieId) filtros.id_institucion_edu = ieId;

      if (vistaDistrital) filtros.vista_distrital = true;

      Object.assign(filtros, overrideFilters);

      Object.keys(filtros).forEach((key) => {
        if (
          filtros[key] === "" ||
          filtros[key] === null ||
          filtros[key] === undefined ||
          filtros[key] === false
        ) {
          delete filtros[key];
        }
      });
      
      if (!filtros.id_institucion_edu) {
        delete filtros.id_institucion_edu;
      }

      if (!filtros.vista_distrital) {
        delete filtros.vista_distrital;
      }

      setAppliedSearch(filtros.q || "");

      const res = await buscarSesiones({
        filtros,
        page: newPage,
        rowsPerPage: newRowsPerPage,
      });

      setFiltered(res.data.items || []);
      setTotal(res.data.total_count || 0);
    } catch (e) {
      console.error("Error al buscar sesiones", e);
      setFiltered([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };
  return {
    search, setSearch,
    appliedSearch,
    usuario, setUsuario,
    fechaDesde, setFechaDesde,
    fechaHasta, setFechaHasta,
    nivelId, setNivelId,
    gradoId, setGradoId,
    areaId, setAreaId,

    regionId, setRegionId,
    provinciaId, setProvinciaId,
    distritoId, setDistritoId,
    tipoSesion, setTipoSesion,
    estado, setEstado,

    ieId, setIeId,
    instituciones,

    vistaDistrital, setVistaDistrital,

    usuarios,
    showAuthorFilter,
    niveles,
    grados,
    areas,
    regiones,
    provincias,
    distritos,

    filtered,
    total,
    loading,

    page, setPage,
    rowsPerPage, setRowsPerPage,

    handleSearch,
  };
};
